// A chat's knowledge graph laid over one trace's lanes.
//
// A chat investigation keeps what it has established as a graph of typed
// anchors, the relations its rows asserted, and the claims it cited against
// them (investigationGraph.js). A trace reading is where many of those anchors
// happened. This module walks the graph from the trace's own session node and
// places what the walk reaches on the lanes, so a claim can be read against
// the turns it rests on.
//
// The walk takes the session node's direct neighbours (the processes,
// messages and prompts its rows tied to it) and two ties that need no session
// edge: a message whose id a loaded turn carries (the id is the tie), and an
// anchor a claim cites together with this session (the claim is the tie).
// Nothing else is placed: a command line that recurs in another session's rows
// is not this trace's process.
//
// Placement says how it was found, strongest first:
//   message  the turn carrying the message id;
//   command  the shell call whose command the endpoint recorded, by the rules
//            the system band uses (traceEndpointMatch), with the calls limited
//            to those at or before the process's time when the graph kept one;
//            a command run more than once lands on its last run before the
//            process started, and any other tie between turns is left
//            unplaced, not guessed;
//   time     the turn the turn clock dates the process to, a turn with no cell,
//            when no single call names it but its time is known.
// An anchor none of these reach is listed and counted, never marked: a mark on
// the axis promises something to read at that turn.

import { LABEL_MAX } from "./investigationGraph.js";
import { EVIDENCE_MARK } from "./investigationGraphView.js";
import { matchCommand, normalizeSpace, programFamily, programName, shellCommandCandidates, shellWrapperScript } from "./traceEndpointMatch.js";
import { unwrapHarnessCommand } from "./traceSystemStream.js";
import { shellToolCommand } from "./shellToolCall.js";
import { buildTurnTimes } from "./traceTurnTimes.js";

// The chat whose graph a trace tab wears, and one claim to arrive on. The key
// is the chat's persistent id (graphKeyFor): an id, never content, since a URL
// travels further than the browser store the graph lives in.
export const GRAPH_FOCUS_PARAM = "kg";
export const GRAPH_CLAIM_PARAM = "kgc";

const KEY_RE = /^[A-Za-z0-9._:-]{1,96}$/;
const CLAIM_RE = /^[A-Za-z0-9._:-]{1,160}$/;

export function parseGraphFocus(raw) {
    const text = String(raw ?? "").trim();
    return KEY_RE.test(text) && text !== "default" ? text : null;
}

export function parseGraphClaimFocus(raw) {
    const text = String(raw ?? "").trim();
    return CLAIM_RE.test(text) ? text : null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Ids arrive hyphenated from the analytics rows and from the conversation
// record alike, in a case neither promises.
const canon = (v) => String(v ?? "").trim().toLowerCase();

export function isTraceSessionId(id) {
    return UUID_RE.test(String(id ?? "").trim());
}

// The session ids a trace answers to: its conversation and, for a request tab,
// the request itself. Either can be the id a chat's rows named.
export function traceSessionIds(trace) {
    const out = new Set();
    for (const v of [trace?.sessionId, trace?.id]) {
        const id = canon(v);
        if (isTraceSessionId(id)) out.add(id);
    }
    return out;
}

const kindOf = (key) => {
    const i = String(key || "").indexOf(":");
    return i > 0 ? String(key).slice(0, i) : null;
};

// Tool-use block id -> the lane its call is drawn on, from the lanes model's
// own items (the first home wins, as it does for the joins census).
export function toolUseLanes(model, turns) {
    const out = new Map();
    for (const g of model?.groups || []) {
        for (const lane of g?.lanes || []) {
            for (const it of lane?.items || []) {
                if (it?.turnIndex == null || it?.blockIndex == null) continue;
                const b = turns?.[it.turnIndex]?.blocks?.[it.blockIndex];
                if (b?.type === "tool_use" && b.id && !out.has(b.id)) out.set(b.id, { lane: lane.id, turn: it.turnIndex });
            }
        }
    }
    return out;
}

// Every shell call in the loaded turns, as the candidates a process's command
// line is compared with, indexed so a process is compared only with calls that
// could match it: by the candidate's whole text (the verbatim ranks) and by its
// program (the argument ranks, which require the same program or interpreter).
// `steps` counts the commands a call's script runs, so a match on one of
// several can say so.
const programKey = (token) => programFamily(programName(token));

// Lanes draws a code-mode cell as one item, not the calls its program makes,
// so a call read out of a cell (traceCodeModeCells) is at home on the cell.
function cellHome(block, laneOfToolUse) {
    if (!block?.virtual) return null;
    const parent = block.codeMode?.parent || String(block.id || "").split("#")[0];
    return (parent && laneOfToolUse?.get(parent)) || null;
}

function shellCallIndex(turns, laneOfToolUse) {
    const calls = [];
    const byText = new Map();
    const byProgram = new Map();
    const add = (map, key, call) => {
        if (!key) return;
        const list = map.get(key);
        if (!list) map.set(key, [call]);
        else if (list[list.length - 1] !== call) list.push(call);
    };
    (turns || []).forEach((t, turn) => {
        (t?.blocks || []).forEach((b, block) => {
            if (b?.type !== "tool_use") return;
            const command = shellToolCommand(b.name, b.input);
            if (!command) return;
            const candidates = shellCommandCandidates(command);
            if (!candidates.length) return;
            const home = (b.id && laneOfToolUse?.get(b.id)) || cellHome(b, laneOfToolUse);
            const steps = candidates.filter((c) => c.argv).length;
            const call = { turn, block, toolUseId: b.id || null, lane: home?.lane || null, command, candidates, steps };
            calls.push(call);
            for (const cand of candidates) {
                add(byText, cand.text, call);
                if (cand.argv?.length) add(byProgram, programKey(cand.argv[0]), call);
            }
        });
    });
    return { calls, byText, byProgram };
}

const firstToken = (line) => {
    const m = /^("([^"]*)"|'([^']*)'|(\S+))/.exec(line);
    return m ? (m[2] ?? m[3] ?? m[4] ?? "") : "";
};

// The forms of an observed command line worth comparing: the line itself and,
// for a harness-wrapped line, the command inside the wrapper, which is what
// the agent ran and what a reader should be shown.
function observedForms(line) {
    const forms = [];
    const push = (f, wrapped) => {
        const text = normalizeSpace(f);
        if (text && !forms.some((x) => x.text === text)) forms.push({ text, wrapped });
    };
    push(line, false);
    const inner = unwrapHarnessCommand(line);
    if (inner) push(inner, true);
    return forms;
}

export function processCommandOf(anchorOrNode) {
    const line = anchorOrNode?.command || anchorOrNode?.attrs?.command || anchorOrNode?.label || "";
    const inner = unwrapHarnessCommand(line);
    return inner ? normalizeSpace(inner) : line;
}

// The calls a process's command line corresponds to, strongest basis only. A
// line that may have been cut short never lets a script's variable stand for
// its missing end.
function matchProcess(line, index, allowed, { whole = true } = {}) {
    let best = null;
    for (const { text: form, wrapped } of observedForms(line)) {
        const pool = new Set([
            ...(index.byText.get(form) || []),
            ...(index.byText.get(shellWrapperScript(form) || "") || []),
            ...(index.byProgram.get(programKey(firstToken(form))) || []),
        ]);
        for (const call of pool) {
            if (allowed && !allowed.has(call)) continue;
            for (const cand of call.candidates) {
                const hit = matchCommand(form, cand);
                if (!hit || (hit.filled && !whole)) continue;
                const step = cand.argv && call.steps > 1 ? `${hit.basis}, one step of the call's script` : hit.basis;
                const basis = wrapped ? `${step}, inside the harness's shell wrapper` : step;
                if (!best || hit.rank > best.rank) best = { rank: hit.rank, basis, calls: [call] };
                else if (hit.rank === best.rank && !best.calls.includes(call)) best.calls.push(call);
            }
        }
    }
    return best;
}

// The turn an instant falls in: the first dated turn at or after it, the reply
// its work fed (bucketEventsByTurn's rule). Null before the first dated turn or
// after the last, where the clock says nothing.
export function turnAtTime(times, at) {
    if (!Array.isArray(times) || !Number.isFinite(at)) return null;
    let first = null;
    for (let t = 0; t < times.length; t++) {
        if (!Number.isFinite(times[t])) continue;
        if (first == null) first = times[t];
        if (times[t] >= at) return at >= first ? t : null;
    }
    return null;
}

// Where a process seen at `at` sits on the turn clock: `after`, the first dated
// turn at or after it (null past the last), and `upto`, the last turn a call
// that started it can be in -- `after` itself included, the system band's
// slack for a capture clock that trails the machine's. Undated turns keep their
// place in the order, so a call in one is judged by its position.
function clockBounds(times, at) {
    let after = null;
    let dated = false;
    for (let t = 0; t < times.length; t++) {
        if (!Number.isFinite(times[t])) continue;
        dated = true;
        if (times[t] >= at) { after = t; break; }
    }
    if (!dated) return null;
    return { after, upto: after ?? times.length - 1 };
}

const MAX_CELLS_PER_ANCHOR = 3;

// A message that says something is what it said, so it lands on the
// conversation's own row; the calls in the same turn are not what a claim about
// the message rests on. A message of tool results has no words of its own, and
// lands on the cells of the calls it answers.
const CONVERSATION_LANES = new Set(["prompt:all", "reply:all"]);

function messageCells(input, turn) {
    const cells = [];
    const segs = input?.segments || [];
    const said = segs.filter((seg) => CONVERSATION_LANES.has(seg?.lane));
    for (const seg of said.length ? said : segs) {
        if (!seg?.lane) continue;
        const cellTurn = Number.isInteger(seg.cellTurn) ? seg.cellTurn : turn;
        if (!cells.some((c) => c.lane === seg.lane && c.turn === cellTurn)) cells.push({ lane: seg.lane, turn: cellTurn });
        if (cells.length >= MAX_CELLS_PER_ANCHOR) break;
    }
    return cells;
}

function placeMessage(node, ctx) {
    const turns = ctx.turnsByMessage.get(canon(node.id)) || [];
    if (!turns.length) return null;
    return {
        basis: "message",
        detail: "the turn carrying this message",
        turn: turns[0],
        turns,
        cells: turns.flatMap((t) => messageCells(ctx.turnInputs?.[t], t)).slice(0, MAX_CELLS_PER_ANCHOR),
    };
}

function placeProcess(node, ctx) {
    const line = node.attrs?.command || node.label || "";
    const whole = Boolean(node.attrs?.command) || (node.label || "").length < LABEL_MAX;
    const at = Number.isFinite(node.attrs?.at) ? node.attrs.at : null;
    const dated = at != null && ctx.clock ? ctx.clock : null;
    const bounds = dated ? clockBounds(dated, at) : null;
    // A process cannot have been started by a call made after it ran.
    const allowed = bounds ? new Set(ctx.shell.calls.filter((c) => c.turn <= bounds.upto)) : null;
    const hit = line ? matchProcess(line, ctx.shell, allowed, { whole }) : null;
    let calls = hit?.calls || [];
    let detail = hit?.basis || null;
    const turnsOf = (list) => [...new Set(list.map((c) => c.turn))].sort((a, b) => a - b);
    let turns = turnsOf(calls);
    // The same command run more than once before the process started: the last
    // run before it is the one that started it. Only for the same command, the
    // verbatim and whole-argument ranks; a shared program and first argument is
    // not the same command, and choosing among those would be a guess.
    if (turns.length > 1 && bounds && hit.rank >= 2) {
        const before = calls.filter((c) => bounds.after == null || c.turn < bounds.after);
        if (before.length) {
            const last = Math.max(...before.map((c) => c.turn));
            calls = before.filter((c) => c.turn === last);
            turns = [last];
            detail = `${hit.basis}, its last run before the process started`;
        }
    }
    if (turns.length === 1) {
        const cells = [];
        for (const c of calls) {
            if (c.lane && !cells.some((x) => x.lane === c.lane)) cells.push({ lane: c.lane, turn: c.turn });
        }
        return { basis: "command", detail, rank: hit.rank, turn: turns[0], turns, cells: cells.slice(0, MAX_CELLS_PER_ANCHOR) };
    }
    const byTime = dated ? turnAtTime(dated, at) : null;
    if (byTime != null) {
        return {
            basis: "time",
            detail: turns.length > 1
                ? `its command matches calls in ${turns.length} turns; placed by its time`
                : "no call names its command; placed by its time",
            turn: byTime,
            turns: [byTime],
            cells: [],
            at,
        };
    }
    return turns.length > 1 ? { basis: null, detail: `its command matches calls in ${turns.length} turns`, turn: null, turns: [], cells: [], ambiguous: turns } : null;
}

// The relations an anchor carries to things that are not on the axis: the
// endpoint a process ran on, the account it ran as, the host it reached.
function relationsOf(graph, key, skip) {
    const out = [];
    for (const e of graph.edges.values()) {
        const other = e.from === key ? e.to : e.to === key ? e.from : null;
        if (!other || skip.has(other)) continue;
        const node = graph.nodes.get(other);
        if (!node || node.kind === "window") continue;
        if (out.some((r) => r.key === other && r.via === e.kind)) continue;
        out.push({ via: e.kind, reverse: e.to === key, key: other, kind: node.kind, id: node.id, label: node.label || null });
        if (out.length >= 8) break;
    }
    return out;
}

const PLACEABLE = new Set(["process", "message", "prompt"]);

// A message as a reader knows it: who said it and how it starts, or which
// calls it made, or whose result it carries. A tool result's content is never
// quoted here: it is output, and the trace's own readings show it where it is.
function describeMessage(turns, turnIndex, toolNameById) {
    const turn = Number.isInteger(turnIndex) ? turns?.[turnIndex] : null;
    if (!turn) return null;
    const blocks = turn.blocks || [];
    const text = blocks.find((b) => b?.type === "text" && typeof b.text === "string" && b.text.trim())?.text;
    if (text) {
        const words = normalizeSpace(text);
        return `${turn.role === "assistant" ? "agent" : "user"}: "${words.length > 90 ? `${words.slice(0, 89)}\u2026` : words}"`;
    }
    const calls = [...new Set(blocks.filter((b) => b?.type === "tool_use" && b.name).map((b) => b.name))];
    if (calls.length) return `agent call${calls.length > 1 ? "s" : ""}: ${calls.join(", ")}`;
    const results = blocks.filter((b) => b?.type === "tool_result");
    if (results.length) {
        const names = [...new Set(results.map((b) => toolNameById.get(b.tool_use_id) || "a call"))];
        return `result${results.length > 1 ? "s" : ""} of ${names.join(", ")}`;
    }
    return null;
}
const CLAIM_RANK = { supported: 2, insufficient: 1 };

// The overlay for one trace. `turnInputs` is the lane-hinted text per turn the
// joins census reads (turnsToJoinInputs); it carries each turn's message id and
// the cells its blocks occupy. Returns null when the graph says nothing about
// this trace.
export function buildTraceGraphOverlay(graph, { trace, turns = [], turnInputs = [], laneOfToolUse = null } = {}) {
    if (!graph?.nodes?.size) return null;
    const ids = traceSessionIds(trace);
    const sessionKeys = new Set();
    for (const n of graph.nodes.values()) if (n.kind === "session" && ids.has(canon(n.id))) sessionKeys.add(n.key);

    const turnsByMessage = new Map();
    (turnInputs || []).forEach((t, i) => {
        const id = canon(t?.messageId);
        if (!id) return;
        if (!turnsByMessage.has(id)) turnsByMessage.set(id, []);
        turnsByMessage.get(id).push(i);
    });

    // The walk. Each anchor keeps why it belongs to this trace.
    const ties = new Map();
    const tie = (key, why) => {
        const node = graph.nodes.get(key);
        if (!node || !PLACEABLE.has(node.kind)) return;
        if (!ties.has(key)) ties.set(key, []);
        if (!ties.get(key).includes(why)) ties.get(key).push(why);
    };
    for (const e of graph.edges.values()) {
        if (sessionKeys.has(e.to)) tie(e.from, "session");
        else if (sessionKeys.has(e.from)) tie(e.to, "session");
    }
    for (const n of graph.nodes.values()) if (n.kind === "message" && turnsByMessage.has(canon(n.id))) tie(n.key, "message");
    // A claim naming this session vouches for the anchors it cites beside it,
    // except an anchor its rows tied to another session: a claim can relate two
    // sessions, and each anchor belongs to the session its rows put it in.
    const elsewhere = new Set();
    for (const e of graph.edges.values()) {
        const [a, b] = [e.from, e.to];
        if (kindOf(b) === "session" && !sessionKeys.has(b) && kindOf(a) !== "session") elsewhere.add(a);
        if (kindOf(a) === "session" && !sessionKeys.has(a) && kindOf(b) !== "session") elsewhere.add(b);
    }
    for (const c of graph.claims || []) {
        if (!(c.evidence || []).some((k) => sessionKeys.has(k))) continue;
        for (const k of c.evidence || []) if (ties.has(k) || !elsewhere.has(k)) tie(k, "claim");
    }
    if (!sessionKeys.size && !ties.size) return null;

    const clock = buildTurnTimes(turns)?.times || null;
    const ctx = {
        turnInputs,
        turnsByMessage,
        clock,
        shell: shellCallIndex(turns, laneOfToolUse),
    };
    const skip = new Set(sessionKeys);
    const toolNameById = new Map();
    for (const t of turns || []) for (const b of t?.blocks || []) if (b?.type === "tool_use" && b.id) toolNameById.set(b.id, b.name || null);
    const anchors = [];
    for (const [key, why] of ties) {
        const node = graph.nodes.get(key);
        const placement = node.kind === "message" ? placeMessage(node, ctx) : node.kind === "process" ? placeProcess(node, ctx) : null;
        anchors.push({
            key,
            kind: node.kind,
            id: node.id,
            label: node.label || null,
            command: node.attrs?.command || null,
            // What a reader is shown: a wrapped process as the command inside
            // its wrapper, anything else as its label.
            display: (node.kind === "process"
                ? processCommandOf(node)
                : node.kind === "message"
                    ? describeMessage(turns, placement?.turns?.[0], toolNameById) || `message ${String(node.id).slice(0, 8)}`
                    : node.label) || null,
            marks: [...(node.evidence || [])].map((e) => EVIDENCE_MARK[e] || "?").sort().join(""),
            ties: why,
            placement: placement?.turn != null ? placement : null,
            unplaced: placement?.turn != null ? null : (placement?.detail || (node.kind === "prompt" ? "a prompt summary has no turn of its own" : node.kind === "message" ? "not in the loaded turns" : "no call in the loaded turns names it")),
            relations: relationsOf(graph, key, skip),
            claims: [],
        });
    }
    const anchorByKey = new Map(anchors.map((a) => [a.key, a]));

    // Claims, by what they cite: this trace's anchors (placed or not), this
    // session itself, or the things this session is related to (its endpoint,
    // its account), alone or beside another session.
    const sessionNeighbours = new Set();
    for (const e of graph.edges.values()) {
        if (sessionKeys.has(e.from)) sessionNeighbours.add(e.to);
        if (sessionKeys.has(e.to)) sessionNeighbours.add(e.from);
    }
    const claims = [];
    for (const c of graph.claims || []) {
        const evidence = c.evidence || [];
        const cited = evidence.map((k) => anchorByKey.get(k)).filter(Boolean);
        const citesSession = evidence.some((k) => sessionKeys.has(k));
        const related = evidence.filter((k) => sessionNeighbours.has(k) && !anchorByKey.has(k));
        const otherSessions = evidence.filter((k) => kindOf(k) === "session" && !sessionKeys.has(k)).map((k) => graph.nodes.get(k)?.id).filter(isTraceSessionId);
        if (!cited.length && !citesSession && !related.length) continue;
        const placed = cited.filter((a) => a.placement);
        const cells = [];
        for (const a of placed) for (const cell of a.placement.cells) if (!cells.some((x) => x.lane === cell.lane && x.turn === cell.turn)) cells.push(cell);
        const claim = {
            id: c.id,
            kind: c.kind,
            status: c.status,
            bearing: c.bearing === "context" ? "context" : "answers",
            statement: String(c.statement || ""),
            reason: c.reason || null,
            evidence,
            anchors: cited.map((a) => a.key),
            turns: [...new Set(placed.map((a) => a.placement.turn))].sort((a, b) => a - b),
            cells,
            // A claim about the trace's own machine or account is about the
            // trace; one that brings in another session is about that one.
            here: cited.length > 0 || citesSession || (related.length > 0 && !otherSessions.length),
            otherSessions,
            at: Number.isFinite(c.at) ? c.at : null,
            // The chat's own claims, and those an expand reader wrote for it.
            author: c.source?.leaf ? "reader" : "chat",
        };
        for (const a of cited) a.claims.push(claim.id);
        claims.push(claim);
    }
    const claimById = new Map(claims.map((c) => [c.id, c]));
    for (const a of anchors) {
        let best = 0;
        for (const id of a.claims) best = Math.max(best, CLAIM_RANK[claimById.get(id)?.status] || 0);
        a.tone = best === 2 ? "supported" : best === 1 ? "insufficient" : "anchor";
    }

    const byTurn = new Map();
    for (const a of anchors) {
        if (!a.placement) continue;
        for (const t of a.placement.turns) {
            if (!byTurn.has(t)) byTurn.set(t, []);
            byTurn.get(t).push(a.key);
        }
    }
    return {
        sessionKeys: [...sessionKeys],
        anchors,
        claims,
        byTurn,
        placedCount: anchors.filter((a) => a.placement).length,
        locatedClaims: claims.filter((c) => c.turns.length).length,
    };
}

const TONE_WEIGHT = { supported: "clear", insufficient: "likely", anchor: "possible" };
const TONE_RANK = { supported: 2, insufficient: 1, anchor: 0 };

function anchorsIn(overlay, turn, through = turn) {
    const keys = new Set();
    for (let t = turn; t <= Math.max(turn, through); t++) for (const k of overlay?.byTurn?.get(t) || []) keys.add(k);
    return (overlay?.anchors || []).filter((a) => keys.has(a.key));
}

const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}\u2026` : s);

// One mark per turn where claims rest, weighted by the strongest of them: a
// turn under a supported claim is drawn firmly, one under only unsupported
// claims lighter. Anchors no claim cites mark nothing -- they are what the walk
// reached, not what the chat concluded. `skipTurns` are turns a comment pin
// already stands on, so the strip never draws a mark under its own pin.
export function graphStripMarks(overlay, { skipTurns = null } = {}) {
    const out = [];
    for (const turn of [...(overlay?.byTurn?.keys() || [])].sort((a, b) => a - b)) {
        if (skipTurns?.has(turn)) continue;
        const cited = anchorsIn(overlay, turn).filter((a) => a.claims.length);
        if (!cited.length) continue;
        const top = cited.reduce((best, a) => (TONE_RANK[a.tone] > TONE_RANK[best.tone] ? a : best), cited[0]);
        const claim = overlay.claims.find((c) => top.claims.includes(c.id));
        out.push({ turn, weight: TONE_WEIGHT[top.tone], label: clip(claim?.statement || `${top.kind} ${top.display || top.id}`, 140) });
    }
    return out;
}

// Cell pins: one per (lane, turn) an anchor was placed in, toned by the
// strongest claim resting on the cell.
export function graphCellPins(overlay) {
    const byCell = new Map();
    for (const a of overlay?.anchors || []) {
        // Evidence, not everything the walk reached: a dot says a claim rests here.
        if (!a.claims.length) continue;
        for (const c of a.placement?.cells || []) {
            const key = `${c.lane} ${c.turn}`;
            const cur = byCell.get(key);
            if (!cur) byCell.set(key, { lane: c.lane, turn: c.turn, tone: a.tone, count: 1, title: a.display || a.id });
            else {
                cur.count += 1;
                if (TONE_RANK[a.tone] > TONE_RANK[cur.tone]) { cur.tone = a.tone; cur.title = a.display || a.id; }
            }
        }
    }
    return [...byCell.values()];
}

// What the dock shows for a turn (or a folded mark's reach): the anchors
// placed there and the claims resting on them, claims first by strength.
export function graphAtTurn(overlay, turn, through = turn) {
    const anchors = anchorsIn(overlay, turn, through);
    const ids = new Set(anchors.flatMap((a) => a.claims));
    const claims = (overlay?.claims || []).filter((c) => ids.has(c.id))
        .sort((a, b) => (CLAIM_RANK[b.status] || 0) - (CLAIM_RANK[a.status] || 0));
    return { anchors, claims };
}

// A claim's evidence as links between the cells it rests on, in turn order, so
// the claim reads as the path through the conversation it asserts.
export function claimCellLinks(overlay, claimId) {
    const claim = (overlay?.claims || []).find((c) => c.id === claimId);
    if (!claim || claim.cells.length < 2) return [];
    const cells = [...claim.cells].sort((a, b) => a.turn - b.turn || String(a.lane).localeCompare(String(b.lane)));
    const out = [];
    for (let i = 1; i < cells.length; i++) {
        out.push({ from: { laneId: cells[i - 1].lane, turn: cells[i - 1].turn }, to: { laneId: cells[i].lane, turn: cells[i].turn }, tier: "claim" });
    }
    return out;
}

// The strip's own line, short enough for the rail beside its control at the
// default width; the hover has the whole one.
export function graphStripSummary(overlay) {
    if (!overlay) return "";
    const here = overlay.claims.filter((c) => c.here).length;
    return `${here} claim${here === 1 ? "" : "s"}`;
}

export function graphOverlaySummary(overlay) {
    if (!overlay) return "";
    const here = overlay.claims.filter((c) => c.here).length;
    const parts = [];
    if (here) parts.push(`${here} claim${here === 1 ? "" : "s"} about this trace${overlay.locatedClaims ? `, ${overlay.locatedClaims} on the axis` : ""}`);
    parts.push(`${overlay.placedCount} of ${overlay.anchors.length} anchor${overlay.anchors.length === 1 ? "" : "s"} placed`);
    const related = overlay.claims.length - here;
    if (related) parts.push(`${related} about related traces`);
    return parts.join(" \u00b7 ");
}

// -- the comment layer --------------------------------------------------------

// Where each claim is pinned as a comment: on the first cell its evidence was
// placed on, or, for a claim placed on turns but on no cell, on the graph strip
// over its first turn. Claims pinned to the same place share one pin, the way
// replies share a thread. A claim with nothing on the axis has no pin; the dock
// lists it.
export function graphBadgeAnchors(overlay) {
    const byKey = new Map();
    for (const c of overlay?.claims || []) {
        if (!c.turns.length) continue;
        const cell = [...c.cells].sort((a, b) => a.turn - b.turn || String(a.lane).localeCompare(String(b.lane)))[0] || null;
        const key = cell ? `cell ${cell.lane} ${cell.turn}` : `turn ${c.turns[0]}`;
        if (!byKey.has(key)) byKey.set(key, { id: key, lane: cell?.lane ?? null, turn: cell ? cell.turn : c.turns[0], claims: [] });
        byKey.get(key).claims.push(c.id);
    }
    return [...byKey.values()];
}

// Pins closer than a pin's own size merge into one, so a zoomed-out axis shows
// a count instead of a stack; zooming in splits them again. Points are in plane
// pixels, and a pin on the strip never merges with one on a lane.
export function clusterBadges(points, { dx = 18, dy = 14 } = {}) {
    const sorted = [...(points || [])].sort((a, b) => a.y - b.y || a.x - b.x);
    const out = [];
    for (const p of sorted) {
        const hit = out.find((c) => c.strip === p.strip && Math.abs(c.x - p.x) < dx && Math.abs(c.y - p.y) < dy);
        if (hit) {
            hit.members.push(p.id);
            for (const id of p.claims) if (!hit.claims.includes(id)) hit.claims.push(id);
        } else {
            out.push({ ...p, members: [p.id], claims: [...p.claims] });
        }
    }
    return out;
}

// Which of a graph's claims the reader has seen, per chat, so a claim the chat
// writes while the reader is away (or after they reply) comes back marked new.
// The first look at a graph takes everything already there as seen: new means
// new since you last looked, not new to you. A per-browser convenience; a
// storage that throws or is absent just means nothing is marked.
export const GRAPH_SEEN_PREFIX = "origin-graph-seen-v1";
const SEEN_MAX = 1000;

export function createSeenStore({ storage = null, keyFor = (graphKey) => `${GRAPH_SEEN_PREFIX}:${graphKey}` } = {}) {
    const resolve = () => {
        if (storage) return storage;
        try { return globalThis.localStorage || null; } catch { return null; }
    };
    const read = (graphKey) => {
        try {
            const raw = resolve()?.getItem(keyFor(graphKey));
            return raw ? new Set(JSON.parse(raw)) : null;
        } catch { return null; }
    };
    const write = (graphKey, ids) => {
        try { resolve()?.setItem(keyFor(graphKey), JSON.stringify([...ids].slice(-SEEN_MAX))); } catch { /* a convenience only */ }
    };
    return {
        // The seen set, taking a first look's claims as its baseline.
        baseline(graphKey, claimIds) {
            const seen = read(graphKey);
            if (seen) return seen;
            const first = new Set(claimIds || []);
            write(graphKey, first);
            return first;
        },
        markSeen(graphKey, claimIds) {
            const seen = read(graphKey) || new Set();
            for (const id of claimIds || []) seen.add(id);
            write(graphKey, seen);
            return seen;
        },
    };
}

// A reply typed on a claim's card, as the message the chat receives: the
// reader's words first, then which trace and which claims they were looking at,
// so the chat's agent can find them in its own graph.
export function claimReplyText({ text, claims = [], traceId = null, turnLabel = null } = {}) {
    const words = String(text || "").trim();
    if (!words) return "";
    const about = claims.map((c) => `${c.id} ("${clip(String(c.statement || ""), 160)}")`).join(", ");
    const where = [traceId ? `trace ${traceId}` : null, turnLabel != null ? `turn ${turnLabel}` : null].filter(Boolean).join(", ");
    return `${words}\n\n(Asked from the Lanes view${where ? ` of ${where}` : ""}, about claim${claims.length === 1 ? "" : "s"} ${about}.)`;
}

// How long ago, in the few characters a comment header has room for.
export function agoText(at, now = Date.now()) {
    if (!Number.isFinite(at)) return "";
    const s = Math.max(0, Math.round((now - at) / 1000));
    if (s < 60) return "now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.round(h / 24);
    return d < 14 ? `${d}d` : `${Math.round(d / 7)}w`;
}

// -- the chat side ------------------------------------------------------------

// The sessions a chat's graph can open as traces: each with how many claims
// rest on it or its anchors and how many anchors it ties. Most cited first,
// then the most read into (the most anchors); where neither sets them apart
// (before any claim, after a search that named traces and nothing in them),
// the ones the chat's latest queries named, each query's rows in the order it
// returned them.
// A session id that is not a conversation id (an OS session number that a
// `session_id` column carried) has no trace to open and is left out.
export function graphTraceSessions(graph) {
    if (!graph?.nodes?.size) return [];
    const out = [];
    const named = new Map();
    for (const n of graph.nodes.values()) {
        if (n.kind !== "session" || !isTraceSessionId(n.id)) continue;
        const tied = new Set([n.key]);
        for (const e of graph.edges.values()) {
            if (e.to === n.key && PLACEABLE.has(kindOf(e.from))) tied.add(e.from);
            if (e.from === n.key && PLACEABLE.has(kindOf(e.to))) tied.add(e.to);
        }
        const claims = (graph.claims || []).filter((c) => (c.evidence || []).some((k) => tied.has(k)));
        // A graph saved before `named` was kept: the write that first named
        // it, and the order it was first named in (the sort is stable).
        named.set(n.key, n.named || [n.seq || 0, 0]);
        out.push({
            key: n.key,
            id: n.id,
            label: n.label || null,
            anchors: tied.size - 1,
            claims: claims.length,
            supported: claims.filter((c) => c.status === "supported").length,
        });
    }
    const latest = (a, b) => named.get(b.key)[0] - named.get(a.key)[0] || named.get(a.key)[1] - named.get(b.key)[1];
    return out.sort((a, b) => b.claims - a.claims || b.anchors - a.anchors || latest(a, b));
}

// The trace a claim is about: a session it cites, else the session its cited
// anchors are tied to, when that is one session. Null when it names none or
// several (the reader picks from the trace list instead).
export function claimTraceSession(graph, claim) {
    const evidence = claim?.evidence || [];
    const direct = new Set(evidence
        .filter((k) => kindOf(k) === "session")
        .map((k) => graph?.nodes?.get(k)?.id)
        .filter(isTraceSessionId));
    if (direct.size) return direct.size === 1 ? [...direct][0] : null;
    const found = new Set();
    for (const e of graph?.edges?.values() || []) {
        for (const [a, b] of [[e.from, e.to], [e.to, e.from]]) {
            if (!evidence.includes(a) || kindOf(b) !== "session") continue;
            const id = graph.nodes.get(b)?.id;
            if (isTraceSessionId(id)) found.add(id);
        }
    }
    return found.size === 1 ? [...found][0] : null;
}
